// ─── src/components/dashboard/DynamicChart.tsx ───────────────────────────────

import { useRef, useState, useEffect, useCallback } from "react";
import { X, Download, Loader2 } from "lucide-react";
import { toPng, toSvg } from "html-to-image";
import type { ChartConfig } from "../../types/chartLab";
import type { ChartEntry, CountEntry } from "../../types/stats";
import { toChartEntries } from "../../types/stats";
import { useRegistrationStats } from "../../hooks/useStats";
import { useMentorStats }        from "../../hooks/useStats";
import { useReportLeadStats }    from "../../hooks/useReportLeads";
import { useCommunityCatalystStats } from "../../hooks/useCommunityCatalysts";
import { Donut, Legend, CHART_COLORS } from "./Charts";
import { HorizontalBarChart }   from "./HorizontalBarChart";

// ── Data resolution ───────────────────────────────────────────────────────────

function useChartData(config: ChartConfig): ChartEntry[] | null {
  const regStats  = useRegistrationStats(undefined);
  const menStats  = useMentorStats();
  const repStats  = useReportLeadStats();
  const catStats  = useCommunityCatalystStats();

  if (config.source === "registrations") {
    const s = regStats.data;
    if (!s) return null;
    const total = s.total;

    if (config.metric === "byCity")   return toChartEntries(s.byCity,      total);
    if (config.metric === "byGender") return toChartEntries(s.byGender,    total);
    if (config.metric === "byIdeaTheme") return toChartEntries(s.byIdeaTheme, total);

    if (config.metric === "pitchingIdea") {
      const yes = s.pitchingIdea;
      const no  = total - yes;
      const entries: CountEntry[] = [
        { name: "Pitching",     count: yes },
        { name: "Not Pitching", count: no  },
      ].filter((e) => e.count > 0);
      return toChartEntries(entries, total);
    }
    if (config.metric === "requestingScholarship") {
      const yes = s.requestingScholarship;
      const entries: CountEntry[] = [
        { name: "Requesting",     count: yes },
        { name: "Not Requesting", count: total - yes },
      ].filter((e) => e.count > 0);
      return toChartEntries(entries, total);
    }
    if (config.metric === "availableAllDays") {
      const yes = s.availableAllDays;
      const entries: CountEntry[] = [
        { name: "Available",     count: yes },
        { name: "Not Available", count: total - yes },
      ].filter((e) => e.count > 0);
      return toChartEntries(entries, total);
    }
  }

  if (config.source === "mentors") {
    const s = menStats.data;
    if (!s) return null;
    const total = s.total;
    if (config.metric === "byTopic")     return toChartEntries(s.byTopic,     total);
    if (config.metric === "byCity")      return toChartEntries(s.byCity,      total);
    if (config.metric === "byFrequency") return toChartEntries(s.byFrequency, total);
    if (config.metric === "byMode")      return toChartEntries(s.byMode,      total);
  }

  if (config.source === "report-leads") {
    const s = repStats.data;
    if (!s) return null;
    const total = s.total;
    const entries: CountEntry[] = Object.entries(s.byReport).map(([name, count]) => ({
      name,
      count,
    }));
    return toChartEntries(entries, total);
  }

  if (config.source === "community-catalysts") {
    const s = catStats.data;
    if (!s) return null;
    const total = s.total;
    const map =
      config.metric === "byGender"     ? s.byGender
      : config.metric === "byTShirtSize" ? s.byTShirtSize
      : config.metric === "bySession"  ? s.bySession
      : s.byGovernorate;
    const entries: CountEntry[] = Object.entries(map).map(([name, count]) => ({
      name,
      count,
    }));
    return toChartEntries(entries, total);
  }

  return null;
}

// ── Download helpers ──────────────────────────────────────────────────────────

function slugify(title: string) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function triggerDownload(dataUrl: string, filename: string) {
  const a = document.createElement("a");
  a.href     = dataUrl;
  a.download = filename;
  a.click();
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  config:   ChartConfig;
  onRemove: (id: string) => void;
}

export function DynamicChart({ config, onRemove }: Props) {
  const data        = useChartData(config);
  const chartRef    = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [showDownload, setShowDownload] = useState(false);
  const [exporting,    setExporting]    = useState(false);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDownload) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDownload(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showDownload]);

  const dateStr  = new Date().toISOString().slice(0, 10);
  const filename = `${slugify(config.title)}-${dateStr}`;

  const handleDownload = useCallback(
    async (format: "png" | "svg") => {
      if (!chartRef.current) return;
      setExporting(true);
      setShowDownload(false);
      try {
        const opts = { backgroundColor: "#111827", pixelRatio: 2 };
        const url  = format === "png"
          ? await toPng(chartRef.current, opts)
          : await toSvg(chartRef.current, { backgroundColor: "#111827" });
        triggerDownload(url, `${filename}.${format}`);
      } catch (err) {
        console.error("Chart export failed:", err);
      } finally {
        setExporting(false);
      }
    },
    [filename],
  );

  const isLoading = data === null;

  return (
    <div className="card flex flex-col gap-4 p-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-white font-semibold text-sm leading-tight">{config.title}</p>
          <p className="text-brand-muted text-xs mt-0.5 capitalize">
            {config.chartType === "donut" ? "Donut chart" : "Bar chart"}
          </p>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Download button */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowDownload((v) => !v)}
              disabled={exporting || isLoading}
              title="Download chart"
              className="p-1.5 rounded-lg text-brand-muted hover:text-white
                         hover:bg-brand-border transition-colors disabled:opacity-40"
            >
              {exporting
                ? <Loader2 size={14} className="animate-spin" />
                : <Download size={14} />
              }
            </button>

            {showDownload && (
              <div className="absolute right-0 top-full mt-1 z-20 bg-brand-card
                              border border-brand-border rounded-xl shadow-xl
                              overflow-hidden min-w-[100px]">
                <button
                  onClick={() => handleDownload("png")}
                  className="w-full px-4 py-2.5 text-sm text-left text-white
                             hover:bg-brand-border transition-colors"
                >
                  PNG
                </button>
                <button
                  onClick={() => handleDownload("svg")}
                  className="w-full px-4 py-2.5 text-sm text-left text-white
                             hover:bg-brand-border transition-colors border-t border-brand-border"
                >
                  SVG
                </button>
              </div>
            )}
          </div>

          {/* Remove button */}
          <button
            onClick={() => onRemove(config.id)}
            title="Remove chart"
            className="p-1.5 rounded-lg text-brand-muted hover:text-red-400
                       hover:bg-red-500/10 transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Chart area */}
      <div ref={chartRef} className="bg-brand-card rounded-xl p-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-52 text-brand-muted text-sm">
            Loading…
          </div>
        ) : data.length === 0 ? (
          <div className="flex items-center justify-center h-52 text-brand-muted text-sm">
            No data available
          </div>
        ) : config.chartType === "donut" ? (
          <div className="flex flex-col gap-4">
            <div className="h-52">
              <Donut data={data} colors={CHART_COLORS} />
            </div>
            <Legend data={data} colors={CHART_COLORS} />
          </div>
        ) : (
          <div style={{ height: Math.max(160, data.length * 32) }}>
            <HorizontalBarChart data={data} />
          </div>
        )}
      </div>
    </div>
  );
}
